import { useSearchParams } from 'react-router-dom';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { usePurchaseOrderApiV1 } from '../../../clients/quipli-service/purchase-order/purchase-order-api-v1';
import { DataTable, DataTableColumn, DataTableRow } from '../../../components/table/data-table';
import { PaginationData } from '../../../clients/quipli-service/pagination/pagination';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { toast } from '../../../util/toasts';
import {
  PurchaseOrder,
  PurchaseOrderSearchRequest,
  PurchaseOrderStatus,
  PurchaseOrderPaymentMethod,
} from '../../../clients/quipli-service/purchase-order/purchase-order';
import { Typography } from '../../../components/typography/typography';
import { QuipliLink } from '../../../components/link/quipli-link';
import { SearchBar } from '../../../components/search-bar/search-bar';
import { PurchaseOrderStatusTransitionButtons } from '../status-transition-button/purchase-order-status-transition-button';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { convertToMoney } from '../../../util/util';
import { Location } from '../../../clients/quipli-service/location/location';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { useAdminApiV1 } from '../../../clients/quipli-service/admin/admin-api-v1';
import { Admin } from '../../../clients/quipli-service/admin/admin';
import { Currency } from 'dinero.js';
import { DateTime } from 'luxon';
import { FiltersModal } from '../../../components/modal/filters-modal';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { SelectForm } from '../../../components/select-form/select-form';
import { ToggleTabNav } from '../../../components/toggle-tab-nav/toggle-tab-nav';
import { Badge } from '../../../components/badge/badge';
import FileSaver from 'file-saver';

// Purchase Order status tabs for ToggleTabNav
const PURCHASE_ORDER_STATUS_TABS = [
  PurchaseOrderStatus.Requested,
  PurchaseOrderStatus.Approved,
  PurchaseOrderStatus.Received,
  PurchaseOrderStatus.Cancelled,
];

export const PurchaseOrderList = () => {
  const { searchPurchaseOrders, listPaymentMethods, exportPurchaseOrders } = usePurchaseOrderApiV1();
  const { listLocations } = useAPI();
  const { getAdminsList } = useAdminApiV1();

  const [isLoading, setLoading] = useState<boolean>(true);
  const [locations, setLocations] = useState<Location[]>([]);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PurchaseOrderPaymentMethod[]>([]);
  const [data, setData] = useState<DataTableRow[]>([]);
  const [pagination, setPagination] = useState<PaginationData>();
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [filtersModalOpen, setFiltersModalOpen] = useState<boolean>(false);
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const [selectedStartDate, setSelectedStartDate] = useState<DateTime | undefined>();
  const [selectedEndDate, setSelectedEndDate] = useState<DateTime | undefined>();
  const [selectedPurchasedById, setSelectedPurchasedById] = useState<number | undefined>();
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | undefined>();

  const columns: DataTableColumn[] = [
    { label: 'PO Number', key: 'poNumber' },
    { label: 'Vendor', key: 'vendor' },
    { label: 'Tel. No.', key: 'phone' },
    { label: 'Total', key: 'total' },
    { label: 'Purchased At', key: 'purchasedAt' },
    { label: 'Purchased By', key: 'purchasedBy' },
    { label: 'Status', key: 'status' },
    { label: 'Actions', key: 'actions' },
    { label: '', key: 'fixedActions', fixedAsLast: true },
  ];

  const [params] = useSearchParams();
  const { request, setRequest, fetchCallback } = useRequestQueryParams<PurchaseOrderSearchRequest>(
    {
      keyword: params.get('keyword') ?? '',
      locationIds: params.get('locationIds') ? params.get('locationIds')!.split(',').map(Number) : undefined,
      statuses: params.get('statuses') ? (params.get('statuses')!.split(',') as PurchaseOrderStatus[]) : undefined,
      purchasedById: params.get('purchasedById') ? parseInt(params.get('purchasedById')!) : undefined,
      paymentMethodId: params.get('paymentMethodId') || undefined,
      startDate: params.get('startDate') ? DateTime.fromISO(params.get('startDate')!).startOf('day') : undefined,
      endDate: params.get('endDate') ? DateTime.fromISO(params.get('endDate')!).endOf('day') : undefined,
      pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 10,
      pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    },
    async () => {
      if (locations.length <= 0) return;

      setLoading(true);
      try {
        const { items, pagination } = await searchPurchaseOrders({
          keyword: request.keyword,
          locationIds: request.locationIds?.length ? request.locationIds : undefined,
          statuses: request.statuses?.length ? request.statuses : undefined,
          purchasedById: request.purchasedById,
          paymentMethodId: request.paymentMethodId,
          startDate: request.startDate?.startOf('day'),
          endDate: request.endDate?.endOf('day'),
          pageSize: request.pageSize,
          pageNumber: request.pageNumber,
        });

        setData(items.map(buildDataRow));
        setPagination(pagination);
      } catch (e: any) {
        toast(e.message.msg ?? e.message ?? 'Failed to fetch purchase orders', 'error');
      } finally {
        setLoading(false);
      }
    },
    [locations],
  );

  // Calculate active filter count
  const activeFilters = useMemo(() => {
    let count = 0;
    if (request.locationIds?.length) count++;
    if (request.startDate) count++;
    if (request.endDate) count++;
    if (request.statuses?.length) count++;
    if (request.purchasedById) count++;
    if (request.paymentMethodId) count++;
    return count;
  }, [request]);

  // Clear all filters
  const clearAll = useCallback(() => {
    setRequest((prevState) => ({
      ...prevState,
      locationIds: undefined,
      statuses: undefined,
      purchasedById: undefined,
      paymentMethodId: undefined,
      startDate: undefined,
      endDate: undefined,
    }));
  }, [setRequest]);

  const buildDataRow = (r: PurchaseOrder): DataTableRow => {
    const currency: Currency = (locations.find((l) => l.id === r.locationId)?.currency || 'USD') as Currency;
    return {
      mobileHeader: (
        <>
          <div>
            <Typography type="text-sm" className="text-quipli-black-800">
              <QuipliLink
                to={`purchase-orders/${r.id}`}
                className="underline underline-offset-1 text-[var(--quipli-black-800)]"
              >
                {`${r.poNumber} - ${r.vendor.name}`}
              </QuipliLink>
            </Typography>
          </div>
        </>
      ),
      fields: {
        poNumber: (
          <QuipliLink to={`purchase-orders/${r.id}`} className="underline underline-offset-1 text-quipli-black-800">
            <Typography type="text-sm" weight="regular" className="text-quipli-black-800">
              {r.poNumber}
            </Typography>
          </QuipliLink>
        ),
        vendor: (
          <QuipliLink to={`vendors/${r.vendor.id}`} className="underline underline-offset-1 text-quipli-black-800">
            <Typography type="text-sm" weight="regular" className="text-quipli-black-800">
              {r.vendor.name}
            </Typography>
          </QuipliLink>
        ),
        phone: (
          <Typography type="text-sm" className="truncate text-quipli-black-800">
            {r.vendor.phone || '-'}
          </Typography>
        ),
        total: (
          <div className="flex flex-col">
            <Typography type="text-sm" className="text-quipli-black-800">
              {convertToMoney(r.subtotal, currency).toFormat()}
            </Typography>
          </div>
        ),
        purchasedAt: (
          <>
            <Typography type="text-sm" className="text-quipli-black-800">
              {r.purchasedAt.toFormat('LLL-dd y')}
            </Typography>
          </>
        ),
        purchasedBy: (
          <Typography type="text-sm" className="text-quipli-black-800 capitalize">
            {r.purchasedBy.adminFullName}
          </Typography>
        ),
        status: (
          <Badge
            text={r.status}
            type={
              r.status === PurchaseOrderStatus.Cancelled
                ? 'danger'
                : r.status === PurchaseOrderStatus.Requested
                ? 'warning'
                : r.status === PurchaseOrderStatus.Approved
                ? 'success'
                : 'default'
            }
          />
        ),
        actions: <PurchaseOrderStatusTransitionButtons order={r} isDisabled={false} reload={fetchCallback} />,
        fixedActions: (
          <QuipliLink to={`purchase-orders/${r.id}`}>
            <Button className="icon-button">
              <Icon icon="Edit" />
            </Button>
          </QuipliLink>
        ),
      },
    };
  };

  // Load initial data - using useCallback to prevent infinite loops
  const loadInitialData = useCallback(async () => {
    try {
      const [locationsData, adminsData, paymentMethodsData] = await Promise.all([
        listLocations(),
        getAdminsList(),
        listPaymentMethods(),
      ]);
      setLocations(locationsData);
      setAdmins(adminsData);
      setPaymentMethods(paymentMethodsData);
    } catch (error) {
      console.error('Failed to load initial data:', error);
      toast('Failed to load filter data', 'error');
    }
  }, []); // Empty dependency array since these functions are stable

  const exportToExcel = async () => {
    if (isExporting) return; // Prevent multiple clicks

    setIsExporting(true);
    try {
      const file = await exportPurchaseOrders({
        keyword: request.keyword,
        locationIds: request.locationIds?.length ? request.locationIds : undefined,
        statuses: request.statuses?.length ? request.statuses : undefined,
        purchasedById: request.purchasedById,
        paymentMethodId: request.paymentMethodId,
        startDate: request.startDate?.startOf('day'),
        endDate: request.endDate?.endOf('day'),
      });

      const exportFileName = `Purchase_Orders_${DateTime.local().toFormat('D').replaceAll('/', '-')}`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';

      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (error) {
      console.error('Failed to export purchase orders:', error);
      toast('Failed to export purchase orders', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    loadInitialData();

    // Initialize filter state from URL params
    setSelectedLocationIds(request.locationIds || []);
    setSelectedStartDate(request.startDate);
    setSelectedEndDate(request.endDate);
    setSelectedPurchasedById(request.purchasedById);
    setSelectedPaymentMethodId(request.paymentMethodId);
  }, [
    loadInitialData,
    request.locationIds,
    request.startDate,
    request.endDate,
    request.purchasedById,
    request.paymentMethodId,
  ]);

  return (
    <div className="px-[10px] pb-[5px]">
      <Typography type="display-sm" weight="medium">
        Manage Purchase Orders
      </Typography>

      {/* Search Bar */}
      <div className="pt-2 flex flex-wrap justify-between gap-2 items-start">
        <div className="order-2 md:order-1 flex gap-2">
          <div className="flex gap-2 flex-wrap">
            <SearchBar
              placeholder="Search by keyword"
              subtitle="PO Number, Vendor Name, Vendor Phone Number, Products/Parts on the PO"
              defaultValue={request.keyword}
              setRequest={setRequest}
              minCharacters={3}
            />
            <Button
              className="button-secondary !border-0 h-[46px] relative"
              onClick={() => setFiltersModalOpen(true)}
              data-cy="filter-purchase-order"
            >
              <Typography
                type="text-md"
                className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
              >
                <Icon icon="FilterLines" stroke="var(--grey-700)" /> Filters
              </Typography>

              {activeFilters > 0 && (
                <Badge className="absolute -right-2 -top-2" text={`${activeFilters}`} size="small" type="yellow" />
              )}
            </Button>

            {activeFilters > 0 && (
              <Button
                className="button-secondary !border-0 h-[46px]"
                onClick={() => clearAll()}
                data-cy="clear-filter-purchase-order"
              >
                <Typography
                  type="text-md"
                  className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
                >
                  Clear Filters
                </Typography>
              </Button>
            )}
          </div>
        </div>
        <div className="order-1 md:order-2 flex gap-2">
          <Button className="button-secondary" onClick={exportToExcel} disabled={isExporting}>
            {isExporting ? (
              <>
                <Icon icon="Loading01" className="animate-spin" />
                <Typography
                  type="text-md"
                  weight="semi-bold"
                  className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
                >
                  Exporting...
                </Typography>
              </>
            ) : (
              <>
                <Icon icon="Export" />
                <Typography
                  type="text-md"
                  weight="semi-bold"
                  className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
                >
                  Export
                </Typography>
              </>
            )}
          </Button>
          <Button className="button-yellow !border-0 h-[46px]" data-cy="create-order">
            <QuipliLink to="purchase-orders/create">
              <Typography
                type="text-md"
                weight="semi-bold"
                className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
              >
                Create
              </Typography>
            </QuipliLink>
          </Button>
        </div>
      </div>

      {/* Status Toggle Navigation */}
      <div className="pt-2 pb-2">
        <ToggleTabNav
          tabs={PURCHASE_ORDER_STATUS_TABS}
          onSelect={({ workFlowStatus }) => {
            setRequest((prevState) => ({
              ...prevState,
              statuses: workFlowStatus?.length ? workFlowStatus : undefined,
            }));
          }}
          getDisplayValue={(status) => status}
          selectedTabs={request.statuses || []}
          className="bg-transparent !mt-0"
        />
      </div>

      <FiltersModal
        isOpen={filtersModalOpen}
        title="Purchase Order Filters"
        onConfirm={() => {
          setFiltersModalOpen(false);
          setRequest((prevState) => ({
            ...prevState,
            locationIds: selectedLocationIds.length > 0 ? selectedLocationIds : undefined,
            startDate: selectedStartDate,
            endDate: selectedEndDate,
            purchasedById: selectedPurchasedById,
            paymentMethodId: selectedPaymentMethodId,
          }));
        }}
        onCancel={() => {
          setFiltersModalOpen(false);
        }}
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 w-full">
            <Typography type="text-md" weight="medium">
              Location
            </Typography>
            <div className="flex gap-2 w-full">
              <SelectForm
                className="w-full break-all"
                options={locations.map((loc) => ({ label: loc.name, value: loc.id.toString() }))}
                onChange={(selected) => {
                  if (selected) {
                    setSelectedLocationIds(selected.map((s) => parseInt(s.value)));
                  } else {
                    setSelectedLocationIds([]);
                  }
                }}
                isClearable={true}
                isMulti={true}
                menuPlacement="bottom"
                placeholder="Select Locations"
                value={
                  selectedLocationIds.length > 0
                    ? selectedLocationIds.map((id) => {
                        const location = locations.find((l) => l.id === id);
                        return { label: location?.name || '', value: id.toString() };
                      })
                    : null
                }
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Typography type="text-md" weight="medium">
              Date Range
            </Typography>
            <div className="flex flex-wrap gap-2">
              <DatePicker
                value={selectedStartDate}
                onChange={(d) => setSelectedStartDate(d)}
                placeholder="Date From"
                clearable
              />
              <DatePicker
                value={selectedEndDate}
                onChange={(d) => setSelectedEndDate(d)}
                placeholder="Date To"
                clearable
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 w-full">
            <Typography type="text-md" weight="medium">
              Purchased By
            </Typography>
            <div className="flex gap-2 w-full">
              <SelectForm
                className="w-full break-all"
                options={admins}
                getOptionLabel={(admin) => admin.adminFullName}
                getOptionValue={(admin) => admin.adminId.toString()}
                onChange={(admin) => setSelectedPurchasedById(admin?.adminId)}
                isClearable={true}
                menuPlacement="auto"
                placeholder="Select Admin"
                value={selectedPurchasedById ? admins.find((admin) => admin.adminId === selectedPurchasedById) : null}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 w-full">
            <Typography type="text-md" weight="medium">
              Payment Method
            </Typography>
            <div className="flex gap-2 w-full">
              <SelectForm
                className="w-full break-all"
                options={paymentMethods}
                getOptionLabel={(method) => method.name}
                getOptionValue={(method) => method.id}
                onChange={(method) => setSelectedPaymentMethodId(method?.id)}
                isClearable={true}
                menuPlacement="auto"
                placeholder="Select Payment Method"
                value={
                  selectedPaymentMethodId
                    ? paymentMethods.find((method) => method.id === selectedPaymentMethodId)
                    : null
                }
              />
            </div>
          </div>
        </div>
      </FiltersModal>

      <div className="pt-5">
        <DataTable
          columnNames={columns}
          data={data}
          pagination={pagination}
          request={request}
          setRequest={setRequest}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};
